import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, Edit, Eye, QrCode, AlertTriangle } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { ShareEventButton } from "@/components/ShareEventButton";
import { Skeleton } from "@/components/ui/skeleton";

const eventDetailLoader = () => import("@/pages/EventDetail");
const editEventLoader = () => import("@/pages/EditEvent");

const EventDetail = lazy(eventDetailLoader);
const EditEvent = lazy(editEventLoader);

// Preload chunks ahead of click. Safe to call repeatedly - dynamic import is cached.
let detailPreloaded = false;
let editPreloaded = false;
export const preloadEventManageModal = (opts?: { edit?: boolean }) => {
  if (!detailPreloaded) {
    detailPreloaded = true;
    eventDetailLoader().catch(() => { detailPreloaded = false; });
  }
  if (opts?.edit && !editPreloaded) {
    editPreloaded = true;
    editEventLoader().catch(() => { editPreloaded = false; });
  }
};

export type EventManageModalPayload = {
  id: string;
  slug?: string | null;
  title: string;
  canManage?: boolean;
  defaultView?: "preview" | "edit";
};

export const EVENT_MANAGE_MODAL_OPEN_EVENT = "openEventManageModal";
export const EVENT_MANAGE_MODAL_CLOSE_EVENT = "closeEventManageModal";
export const EVENT_MANAGE_MODAL_CLOSED_EVENT = "eventManageModalClosed";

export const closeEventManageModal = () => {
  window.dispatchEvent(new CustomEvent(EVENT_MANAGE_MODAL_CLOSE_EVENT));
};

interface EventManageModalProps {
  event: EventManageModalPayload | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ModalView = "preview" | "edit";

const InlineLoader = () => (
  <div className="w-full max-w-5xl mx-auto rounded-2xl border border-border/40 bg-background/70 backdrop-blur-xl shadow-2xl overflow-hidden animate-in fade-in duration-200">
    <div className="grid md:grid-cols-2 gap-0">
      <Skeleton className="w-full h-48 md:h-full md:aspect-square rounded-none" />
      <div className="p-5 md:p-6 space-y-4">
        <Skeleton className="h-7 w-3/4" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-2/3" />
        </div>
        <div className="space-y-2 pt-3">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-5/6" />
        </div>
        <Skeleton className="h-11 w-full rounded-xl mt-4" />
      </div>
    </div>
  </div>
);

const EmbeddedRoute = ({ slugOrId, view, onExitGuardChange }: { slugOrId: string; view: ModalView; onExitGuardChange: (guard: ((action: () => void) => void) | null) => void }) => {
  return (
    <Suspense fallback={<InlineLoader />}>
      {view === "preview" ? (
        <EventDetail embedSlug={slugOrId} embedMode />
      ) : (
        <EditEvent embedSlug={slugOrId} embedMode onExitGuardChange={onExitGuardChange} />
      )}
    </Suspense>
  );
};

// Nested overlays (date/time drawers, selects, popovers) render in their own
// portals. Their outside-clicks must not dismiss this modal.
const isInsideNestedOverlay = (target: EventTarget | null) => {
  const el = target instanceof Element ? target : null;
  if (!el) return false;
  return Boolean(
    el.closest(
      "[data-event-picker], [data-vaul-drawer], [vaul-drawer], [data-radix-popper-content-wrapper], [data-radix-select-content], [data-radix-popover-content]"
    )
  );
};

const paneClass =
  "shrink-0 rounded-xl border border-border/40 bg-background/55 backdrop-blur-2xl shadow-2xl";
const paneButtonClass =
  "h-9 px-3 rounded-xl border-0 bg-transparent hover:bg-foreground/5 text-foreground";

export const EventManageModal = ({ event, open, onOpenChange }: EventManageModalProps) => {
  const navigate = useNavigate();
  const [view, setView] = useState<ModalView>(event?.defaultView ?? "preview");
  const exitGuard = useRef<((action: () => void) => void) | null>(null);
  const registerExitGuard = useRef((guard: ((action: () => void) => void) | null) => {
    exitGuard.current = guard;
  }).current;
  const requestExit = (action: () => void) => {
    if (exitGuard.current) exitGuard.current(action);
    else action();
  };
  useEffect(() => {
    if (!open) return;
    const closeHandler = (e: Event) => {
      const close = () => onOpenChange(false);
      if ((e as CustomEvent<{ completed?: boolean }>).detail?.completed) close();
      else if (exitGuard.current) exitGuard.current(close);
      else close();
    };
    window.addEventListener(EVENT_MANAGE_MODAL_CLOSE_EVENT, closeHandler);
    return () => window.removeEventListener(EVENT_MANAGE_MODAL_CLOSE_EVENT, closeHandler);
  }, [open, onOpenChange]);

  // Reset only when the opened event changes. Render-time ref mutation can
  // discard the requested edit view under StrictMode's repeated renders.
  useEffect(() => {
    setView(open ? event?.defaultView ?? "preview" : "preview");
  }, [event?.id, event?.defaultView, open]);

  if (!event) return null;

  // Never render the edit experience for an event the viewer cannot manage.
  const activeView: ModalView = event.canManage ? view : "preview";

  const slugOrId = event.slug || event.id;
  const eventUrl = `${window.location.origin}/event/${slugOrId}`;

  return (
    <Dialog open={open} onOpenChange={(next) => next ? onOpenChange(true) : requestExit(() => onOpenChange(false))}>
      <DialogContent
        className="w-screen h-[100dvh] max-w-none rounded-none sm:w-[96vw] sm:max-w-[1180px] sm:h-[92vh] sm:rounded-2xl border-0 bg-transparent p-0 shadow-none overflow-hidden [&>button]:hidden"
        onPointerDownOutside={(e) => {
          if (isInsideNestedOverlay(e.target)) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (isInsideNestedOverlay(e.target)) e.preventDefault();
        }}
      >
        <DialogTitle className="sr-only">{event.title}</DialogTitle>
        <div className="flex h-full min-h-0 w-full flex-col gap-3 overflow-x-hidden">
          <div className="flex items-start justify-between gap-2 px-2 pt-2 sm:px-1 sm:pt-1">
            <div className="flex flex-1 flex-wrap items-center gap-2 sm:gap-3">
              {event.canManage ? (
                <>
                  <div className={paneClass}>
                    {activeView === "preview" ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className={paneButtonClass}
                        onClick={() => setView("edit")}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className={paneButtonClass}
                        onClick={() => requestExit(() => setView("preview"))}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Preview
                      </Button>
                    )}
                  </div>

                  <div className={paneClass}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={paneButtonClass}
                      onClick={() => requestExit(() => {
                        onOpenChange(false);
                        navigate(`/manage?tab=scan&eventId=${event.id}`);
                      })}
                    >
                      <QrCode className="h-4 w-4 mr-2" />
                      <span className="hidden sm:inline">Scan Tickets</span>
                      <span className="sm:hidden">Scan</span>
                    </Button>
                  </div>

                  <div className={paneClass}>
                    <ShareEventButton
                      eventTitle={event.title}
                      eventUrl={eventUrl}
                      variant="ghost"
                      size="sm"
                      className={paneButtonClass}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className={paneClass}>
                    <ShareEventButton
                      eventTitle={event.title}
                      eventUrl={eventUrl}
                      variant="ghost"
                      size="sm"
                      className={paneButtonClass}
                    />
                  </div>
                  <div className={paneClass}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`${paneButtonClass} text-destructive hover:text-destructive hover:bg-destructive/10`}
                      onClick={() => {
                        try {
                          sessionStorage.setItem(
                            "pendingModalReport",
                            JSON.stringify({
                              event,
                              returnTo: window.location.pathname + window.location.search,
                            })
                          );
                        } catch {}
                        window.dispatchEvent(new CustomEvent("openEventReportDialog"));
                      }}
                    >
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      Report
                    </Button>
                  </div>
                </>
              )}
            </div>

            <div className={paneClass}>
              <Button
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0 rounded-xl border-0 bg-transparent hover:bg-foreground/5 text-foreground"
                onClick={() => requestExit(() => onOpenChange(false))}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden w-full px-0 pb-1 sm:px-1">
            <EmbeddedRoute key={`${event.id}-${activeView}`} slugOrId={slugOrId} view={activeView} onExitGuardChange={registerExitGuard} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const EventManageModalMount = () => {
  const location = useLocation();
  const [event, setEvent] = useState<EventManageModalPayload | null>(null);
  const [open, setOpen] = useState(false);

  // Restore modal context after sign-in if it was stashed before redirect
  useEffect(() => {
    try {
      const purchaseStash = sessionStorage.getItem("pendingEventPurchaseModal");
      if (purchaseStash) {
        const parsed = JSON.parse(purchaseStash);
        const stashedEvent = parsed?.event as EventManageModalPayload | undefined;
        if (stashedEvent?.id) {
          setEvent(stashedEvent);
          setOpen(true);
        }
        sessionStorage.removeItem("pendingEventPurchaseModal");
        return;
      }

      const stash = sessionStorage.getItem("pendingModalReport");
      if (!stash) return;
      const parsed = JSON.parse(stash);
      const stashedEvent = parsed?.event as EventManageModalPayload | undefined;
      if (!stashedEvent?.id) {
        sessionStorage.removeItem("pendingModalReport");
        return;
      }
      // Reopen the modal with the same event
      setEvent(stashedEvent);
      setOpen(true);
      // After the modal mounts, fire the report trigger so EventDetail picks it up
      const t = setTimeout(() => {
        window.dispatchEvent(new CustomEvent("openEventReportDialog"));
        sessionStorage.removeItem("pendingModalReport");
      }, 400);
      return () => clearTimeout(t);
    } catch {
      sessionStorage.removeItem("pendingEventPurchaseModal");
      sessionStorage.removeItem("pendingModalReport");
    }
  }, [location.key]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<EventManageModalPayload>).detail;
      if (!detail?.id) return;
      setEvent(detail);
      setOpen(true);
    };
    window.addEventListener(EVENT_MANAGE_MODAL_OPEN_EVENT, handler);
    return () => {
      window.removeEventListener(EVENT_MANAGE_MODAL_OPEN_EVENT, handler);
    };
  }, []);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      window.dispatchEvent(new CustomEvent(EVENT_MANAGE_MODAL_CLOSED_EVENT));
    }
  };

  return <EventManageModal event={event} open={open} onOpenChange={handleOpenChange} />;
};

export const openEventManageModal = (payload: EventManageModalPayload) => {
  window.dispatchEvent(new CustomEvent(EVENT_MANAGE_MODAL_OPEN_EVENT, { detail: payload }));
};